import { renderMathToHTML } from "@repo/fika-blog/src/lib/math"
import { MathBlock } from "@repo/fika-blog/src/nodes/math-block"
import { MathInline } from "@repo/fika-blog/src/nodes/math-inline"
import type { NodeViewRendererProps } from "@repo/fika-blog/src/tiptap/core"
import type { Node } from "@repo/fika-blog/src/tiptap/pm/model"
import { Selection } from "@repo/fika-blog/src/tiptap/pm/state"
import type { EditorView } from "@repo/fika-blog/src/tiptap/pm/view"

interface MathNodeConfig {
  baseNode: typeof MathBlock
  tag: "div" | "span"
  dataType: string
  cssPrefix: string
  displayMode: boolean
  inputPlaceholder: string
  emptyPlaceholder: string
}

const blockConfig: MathNodeConfig = {
  baseNode: MathBlock,
  tag: "div",
  dataType: "math",
  cssPrefix: "math-block",
  displayMode: true,
  inputPlaceholder: "E = mc^2",
  emptyPlaceholder: "Math equation",
}

const inlineConfig: MathNodeConfig = {
  baseNode: MathInline,
  tag: "span",
  dataType: "mathInline",
  cssPrefix: "math-inline",
  displayMode: false,
  inputPlaceholder: "x^2",
  emptyPlaceholder: "$",
}

class MathEditView {
  node: Node
  view: EditorView
  getPos: NodeViewRendererProps["getPos"]
  dom: HTMLElement
  input: HTMLTextAreaElement | HTMLInputElement
  preview: HTMLElement
  editing: boolean
  config: MathNodeConfig

  constructor(
    config: MathNodeConfig,
    node: Node,
    view: EditorView,
    getPos: NodeViewRendererProps["getPos"],
  ) {
    this.config = config
    this.node = node
    this.view = view
    this.getPos = getPos
    this.editing = !node.attrs.latex

    this.dom = document.createElement(config.tag)
    this.dom.setAttribute("data-type", config.dataType)
    this.dom.classList.add(`${config.cssPrefix}-editor`)
    this.dom.contentEditable = "false"

    this.input = config.displayMode ? this.createTextarea() : this.createInput()
    this.input.classList.add(`${config.cssPrefix}-input`)
    this.input.value = node.attrs.latex || ""
    if (!config.displayMode && this.input instanceof HTMLInputElement)
      this.updateInputWidth(this.input, this.input.value)
    this.input.placeholder = config.inputPlaceholder
    this.input.addEventListener("input", () => this.handleInput())
    this.input.addEventListener("keydown", event =>
      this.handleKeyDown(event as KeyboardEvent),
    )
    this.input.addEventListener("blur", () => {
      if (this.node.attrs.latex) this.stopEditing()
    })

    this.preview = document.createElement(config.tag)
    this.preview.classList.add(`${config.cssPrefix}-preview`)

    this.dom.appendChild(this.input)
    this.dom.appendChild(this.preview)

    this.dom.addEventListener("click", event => {
      if (!config.displayMode) event.preventDefault()
      if (!this.editing) this.startEditing()
    })

    this.updatePreview()
    this.updateEditingState()

    if (!node.attrs.latex) {
      setTimeout(() => this.input.focus(), config.displayMode ? 50 : 0)
    }
  }

  private createTextarea(): HTMLTextAreaElement {
    const textarea = document.createElement("textarea")
    textarea.rows = 1
    return textarea
  }

  private createInput(): HTMLInputElement {
    const input = document.createElement("input")
    input.type = "text"
    return input
  }

  handleInput() {
    if (!this.config.displayMode && this.input instanceof HTMLInputElement)
      this.updateInputWidth(this.input, this.input.value)

    if (this.config.displayMode) this.autoResize()

    this.updatePreview()
    this.dispatchLatex(this.input.value)
  }

  handleKeyDown(event: KeyboardEvent) {
    const shouldConfirm = this.config.displayMode
      ? event.key === "Enter" && !event.shiftKey
      : event.key === "Enter" || event.key === "Escape"

    const shouldEscape =
      this.config.displayMode && event.key === "Escape" && this.node.attrs.latex

    if (shouldConfirm || shouldEscape) {
      event.preventDefault()
      this.stopEditing()
      this.moveCursorAfter()
    }

    if (event.key === "Backspace" && !this.input.value) {
      event.preventDefault()
      this.deleteNode()
    }
  }

  startEditing() {
    this.editing = true
    this.updateEditingState()
    this.input.focus()
    if (this.config.displayMode) this.autoResize()
  }

  stopEditing() {
    this.editing = false
    this.updateEditingState()
  }

  updateEditingState() {
    const display = this.editing
      ? this.config.displayMode
        ? "block"
        : "inline"
      : "none"
    this.input.style.display = display

    if (!this.config.displayMode)
      this.preview.style.display = this.editing ? "none" : "inline"

    this.dom.classList.toggle(`${this.config.cssPrefix}-editing`, this.editing)
  }

  updatePreview() {
    const latex = this.input.value
    if (!latex.trim()) {
      this.preview.innerHTML = `<span class="${this.config.cssPrefix}-placeholder">${this.config.emptyPlaceholder}</span>`
      return
    }
    this.preview.innerHTML = renderMathToHTML({
      latex,
      displayMode: this.config.displayMode,
    })
  }

  update(node: Node) {
    if (node.type !== this.node.type) return false
    this.node = node

    if (node.attrs.latex !== this.input.value) {
      this.input.value = node.attrs.latex || ""
      if (!this.config.displayMode && this.input instanceof HTMLInputElement)
        this.updateInputWidth(this.input, this.input.value)
      this.updatePreview()
    }

    return true
  }

  selectNode() {
    this.startEditing()
  }

  deselectNode() {
    if (this.node.attrs.latex) this.stopEditing()
  }

  stopEvent(event: Event) {
    if (this.config.displayMode) {
      if (!this.editing) return false
      if (
        event instanceof KeyboardEvent &&
        (event.key === "ArrowUp" || event.key === "ArrowDown")
      )
        return false
    }
    return this.dom.contains(event.target as HTMLElement)
  }

  ignoreMutation() {
    return true
  }

  destroy() {}

  private autoResize() {
    if (this.input instanceof HTMLTextAreaElement) {
      this.input.style.height = "auto"
      this.input.style.height = `${this.input.scrollHeight}px`
    }
  }

  private updateInputWidth(input: HTMLInputElement, value: string) {
    input.style.width = `${Math.max(value.length + 1, 4)}ch`
  }

  private dispatchLatex(latex: string) {
    const pos = this.getPos()
    if (typeof pos !== "number") return
    this.view.dispatch(
      this.view.state.tr.setNodeMarkup(pos, undefined, {
        ...this.node.attrs,
        latex,
      }),
    )
  }

  private moveCursorAfter() {
    const pos = this.getPos()
    if (typeof pos !== "number") return
    const afterPos = pos + this.node.nodeSize
    this.view.dispatch(
      this.view.state.tr.setSelection(
        Selection.near(this.view.state.doc.resolve(afterPos)),
      ),
    )
    this.view.focus()
  }

  private deleteNode() {
    const pos = this.getPos()
    if (typeof pos !== "number") return
    this.view.dispatch(this.view.state.tr.delete(pos, pos + this.node.nodeSize))
    this.view.focus()
  }
}

function createMathEditNode(config: MathNodeConfig) {
  return config.baseNode.extend({
    addNodeView() {
      return (props: NodeViewRendererProps) =>
        new MathEditView(config, props.node, props.editor.view, props.getPos)
    },
  })
}

export function getEditMathBlock() {
  return createMathEditNode(blockConfig)
}

export function getEditMathInline() {
  return createMathEditNode(inlineConfig)
}
